// -------------------------------
// ASSET REQUEST INTERFACES
// -------------------------------
export interface PendingAssetRequest {
    assetCategory?: string;
    assetName?: string;
    assetCategoryId?: number;
    quantity?: number;
    description?: string;
}

export interface PendingDeleteAssetRequest {
    awaitingRequestNumber?: boolean;
}

// -------------------------------
// CREATE ASSET REQUEST — START
// -------------------------------
export const startCreateAssetRequestFlow = (
    setPendingAssetRequest: (data: PendingAssetRequest | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    assetNameFromIntent?: string
) => {
    if (assetNameFromIntent) {
        setPendingAssetRequest({ assetName: assetNameFromIntent });

        pushAIChat(`Which asset category does "${assetNameFromIntent}" belong to?`);
        speak(`Which asset category does ${assetNameFromIntent} belong to?`);
        return;
    }

    setPendingAssetRequest({});
    pushAIChat("Sure! What asset would you like to request?");
    speak("What asset would you like to request?");
};

// -------------------------------
// CREATE ASSET REQUEST — PROCESS
// -------------------------------
export const processPendingAssetRequest = (
    text: string,
    pending: PendingAssetRequest,
    setPendingAssetRequest: (data: PendingAssetRequest | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    employeeId: number
) => {

    // STEP 1 — Asset Name
    if (!pending.assetName) {
        const name = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!name) {
            pushAIChat("Please provide a valid asset name.");
            speak("Please provide a valid asset name.");
            return;
        }

        setPendingAssetRequest({ ...pending, assetName: name });

        pushAIChat(`Which asset category does "${name}" belong to?`);
        speak(`Which asset category does ${name} belong to?`);
            // 🔥 SHOW ASSET CATEGORY SUGGESTIONS
    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "asset-category" }
        })
    );
        return;
    }

// STEP 2 — Asset Category
if (!pending.assetCategory) {
    // ❗ If user typed text manually, accept it
    const category = text.trim();

    if (!category) {
        pushAIChat("Please select an asset category.");
        speak("Please select an asset category.");
        return;
    }

    setPendingAssetRequest({ ...pending, assetCategory: category });

    pushAIChat("How many units do you need?");
    speak("How many units do you need?");
    return;
}


    // STEP 3 — Quantity
    if (!pending.quantity) {
        const qty = Number(text);

        if (!qty || qty <= 0) {
            pushAIChat("Please provide a valid quantity greater than 0.");
            speak("Please provide a valid quantity.");
            return;
        }

        setPendingAssetRequest({ ...pending, quantity: qty });

        pushAIChat("Any description for this request?");
        speak("Any description?");
        return;
    }

    // STEP 4 — Description (FINAL)
    if (!pending.description) {
        setPendingAssetRequest({ ...pending, description: text });

        pushAIChat(`Creating asset request for "${pending.assetName}"...`);
        speak(`Creating asset request for ${pending.assetName}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-asset-request", {
                detail: {
                    assetCategoryName: pending.assetCategory,
                    asset_category_id: pending.assetCategoryId,
                    name: pending.assetName,
                    quantity: pending.quantity,
                    requestDescription: text,
                    employeeId: employeeId
                }
            })
        );

        setPendingAssetRequest(null);
    }
};

// -------------------------------
// DELETE ASSET REQUEST — START
// -------------------------------
export const startDeleteAssetRequestFlow = (
    setPendingDelete: (data: PendingDeleteAssetRequest | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    requestNumberFromIntent?: string
) => {
    if (requestNumberFromIntent) {
        pushAIChat(`Deleting asset request "${requestNumberFromIntent}"...`);
        speak(`Deleting asset request ${requestNumberFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-asset-request", {
                detail: { requestNumber: requestNumberFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingRequestNumber: true });
    pushAIChat("Which asset request would you like to delete?");
    speak("Which asset request would you like to delete?");
};

// -------------------------------
// DELETE ASSET REQUEST — PROCESS
// -------------------------------
export const processPendingDeleteAssetRequest = (
    text: string,
    pending: PendingDeleteAssetRequest,
    setPendingDelete: (data: PendingDeleteAssetRequest | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingRequestNumber) {
        const requestNumber = text.trim();

        if (!requestNumber) {
            pushAIChat("Please provide a valid asset request number.");
            speak("Please provide a valid asset request number.");
            return;
        }

        pushAIChat(`Deleting asset request "${requestNumber}"...`);
        speak(`Deleting asset request ${requestNumber}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-asset-request", {
                detail: { requestNumber }
            })
        );

        setPendingDelete(null);
    }
};

